//
// Copyright (c) 2011-2024 by Curalate, Inc
//

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { updateLabel } from '@apis/gatewayMediaApi';
import { ISearchLabelsResponse } from '@apis/gatewayMediaApiModel';
import {
  labelsQueryKeys,
  manageLabelsQueryKeys,
} from '@content-drawer/contentDrawerConstants';
import { LabelUpdateRequest } from '@content-drawer/filters/labels/confirmations/LabelUpdateConfirmationDialog';
import { checkIsLabelUpdatedOrDeleted } from '@content-drawer/filters/labels/utils/checkIsLabelUpdatedOrDeleted';
import { toastNotificationsActions } from '@legacy-components/common/toast-notifications/toastNotificationsActions';
import { ALTERNATE_TOASTS } from '@legacy-components/common/toast-notifications/toastNotificationsConstants';
import { ToastType } from '@legacy-components/common/toast-notifications/toastNotificationsModel';

export const useUpdateLabelMutation = () => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const mutationFn = async ({ label, newLabel }: LabelUpdateRequest) => {
    const instanceId = await updateLabel(label, newLabel);
    return checkIsLabelUpdatedOrDeleted({ instanceId, label, newLabel });
  };

  return useMutation({
    mutationFn,
    onMutate: ({ label, newLabel }) => {
      // Optimistically update label
      queryClient.setQueryData(
        labelsQueryKeys,
        (oldData?: ISearchLabelsResponse) => {
          const count = oldData?.data.count ?? 0;
          const indexObjects = (oldData?.data.indexObjects ?? []).map(
            (indexObject) => ({
              ...indexObject,
              userTag:
                indexObject.userTag === label ? newLabel : indexObject.userTag,
            }),
          );
          return { data: { count, indexObjects } };
        },
      );
    },
    onSettled: async (succeed, error, { label, newLabel }) => {
      if (!succeed || error) {
        await queryClient.invalidateQueries({
          queryKey: manageLabelsQueryKeys,
        });
        dispatch(
          toastNotificationsActions.addToastNotification({
            title: 'Label Update Failed',
            message: `Unable to update the label "${label}" to "${newLabel}". Please try again or contact Bazaarvoice Support.`,
            type: ToastType.ERROR,
            options: {
              toastTemplate: ALTERNATE_TOASTS.NEW_TOAST,
            },
          }),
        );
      }
    },
  });
};
